import * as THREE from 'three'
import { ImportedMeshTopologyBinding } from '../../../imported-model/topology/internal'
import { readImportedTopologyFaceTriangleRangeBindings } from '../../../imported-model/loaders/topology-source/internal'
import type { ImportedTopologyFaceTriangleRangeBinding } from '../../../imported-model/loaders/types'
import { reconcileTopologySnapshotIdentities } from '../../../utils/feature/buildAuthorityTopologySnapshot'

/**
 * 从独立的 Body 语义和显示 Mesh 映射恢复完整拾取资产。
 *
 * 服务只在导入事务构建期存在。它不创建 GPU 资源、不重新网格化 BRep，也不修改显示 Mesh；
 * 最终数组由运行时资产持有，随 Repository/导入结果生命周期释放。
 */
export class PuppyBodyTopologyRestorer {
  /** 同一 occurrence 的所有材质必须完成收集后才能发布完整 Body。 */
  private readonly entriesByOwner = new Map<string, PuppyBodyTopologyRestoreEntry[]>()

  /**
   * 登记待重建的显示绑定，尚不创建可交互宿主。
   * @param entry 当前显示 Mesh 与完整拓扑的对应关系。
   */
  public add(entry: PuppyBodyTopologyRestoreEntry): void {
    const entries = this.entriesByOwner.get(entry.ownerKey) ?? []
    entries.push(entry)
    this.entriesByOwner.set(entry.ownerKey, entries)
  }

  /** 完成所有 Body 的原子构建；失败时调用方不得激活该拓扑源。 */
  public finalize(): void {
    try {
      for (const entries of this.entriesByOwner.values()) {
        this.restoreBody(entries)
      }
    } finally {
      // 构建器不在运行时继续持有 Mesh、包内快照或未发布的资产。
      this.entriesByOwner.clear()
    }
  }

  /**
   * 读取明确的局部映射。新包读取正式字段，已有包读取仍完整保存的 primitive extras。
   * @param entry 当前 Mesh 绑定。
   * @param entries 同一个 Body 的全部显示来源，用于校验旧同序协议的编号域。
   * @returns 当前 Mesh 的显式局部区间。
   */
  private resolveLocalRanges(
    entry: PuppyBodyTopologyRestoreEntry,
    entries: readonly PuppyBodyTopologyRestoreEntry[],
  ): ImportedTopologyFaceTriangleRangeBinding[] {
    const persisted = entry.displayBinding.topologyFaceTriangleRanges
    if (persisted !== undefined) {
      // 正式字段使用导出 Mesh 编号；GLTFLoader 拆分 primitive 后必须显式重定位。
      return this.projectRanges(persisted, entry.primitiveProjections)
    }
    const extras = entry.mesh.geometry.userData.__topo_face_ranges
    if (extras !== undefined) {
      const ranges = readImportedTopologyFaceTriangleRangeBindings(extras, entry.displayBinding.bodyId)
      if (!Array.isArray(extras) || (extras.length > 0 && ranges.length === 0)) {
        throw new Error(`PuppyCAD 显示拓扑映射无效: body=${entry.runtimeBinding.bodyBindingId}`)
      }
      return ranges
    }

    // 旧原生结果体协议允许从同一源 Mesh 拆 primitive，但不能把多个材质各自的零起点当成 Body 起点。
    const sourceEntries = entries.filter(candidate => candidate.sourceAsset.bodyBindingId === entry.sourceAsset.bodyBindingId)
    if (sourceEntries.length !== entries.length) {
      throw new Error(`PuppyCAD 多显示 Mesh Body 缺少显式拓扑映射: body=${entry.runtimeBinding.bodyBindingId}`)
    }
    const ranges: ImportedTopologyFaceTriangleRangeBinding[] = []
    entry.sourceAsset.topologySnapshot.faces.forEach((face, ordinal) => {
      const triangleRange = entry.sourceAsset.faceTriangleRanges?.[ordinal] ?? face.triangleRange
      if (triangleRange && triangleRange[1] > triangleRange[0]) {
        ranges.push({ faceIndex: face.index, faceRef: face.ref, subEntityId: face.subEntityId, triangleRange })
      }
    })
    const sourceCoverage = sourceEntries.flatMap(candidate => candidate.primitiveProjections.map(projection => projection.sourceRange))
      .sort((left, right) => left[0] - right[0])
    for (const range of ranges) {
      let coveredUntil = range.triangleRange[0]
      for (const coverage of sourceCoverage) {
        if (coverage[0] <= coveredUntil && coverage[1] > coveredUntil) coveredUntil = coverage[1]
      }
      if (coveredUntil < range.triangleRange[1]) {
        throw new Error(`PuppyCAD Body 三角形编号域与显示 Mesh 不一致且缺少显式映射: body=${entry.runtimeBinding.bodyBindingId}`)
      }
    }
    return this.projectRanges(ranges, entry.primitiveProjections)
  }

  /**
   * 把明确归属于导出 Mesh 的范围投影到 GLTFLoader 拆分后的局部 Mesh。
   * @param ranges 导出 Mesh 的局部范围，不是 BRep 的三角形编号。
   * @param projections primitive 的源/目标区间。
   * @returns 当前 Mesh 内可见的面片，允许同一面包含多个不连续片段。
   */
  private projectRanges(
    ranges: readonly ImportedTopologyFaceTriangleRangeBinding[],
    projections: readonly PuppyTopologyPrimitiveProjection[],
  ): ImportedTopologyFaceTriangleRangeBinding[] {
    const result: ImportedTopologyFaceTriangleRangeBinding[] = []
    for (const range of ranges) {
      for (const projection of projections) {
        const start = Math.max(range.triangleRange[0], projection.sourceRange[0])
        const end = Math.min(range.triangleRange[1], projection.sourceRange[1])
        if (end > start) {
          const offset = projection.visibleRange[0] - projection.sourceRange[0]
          result.push({ ...range, triangleRange: [start + offset, end + offset] })
        }
      }
    }
    return result
  }

  /**
   * 重建一个 Body 的完整几何与拓扑快照，全部材质记录严格共享同一份结果。
   * @param entries 同一 Body occurrence 的显示片段。
   */
  private restoreBody(entries: readonly PuppyBodyTopologyRestoreEntry[]): void {
    const first = entries[0]!
    const snapshot = reconcileTopologySnapshotIdentities(first.sourceAsset.topologySnapshot, first.sourceAsset.metadata)
    const meshes = [...new Set(entries.map(entry => entry.mesh))]
    const vertexOffsets = new Map<THREE.Mesh, number>()
    let vertexCount = 0
    for (const mesh of meshes) {
      const position = mesh.geometry.getAttribute('position')
      if (!position) throw new Error(`PuppyCAD 显示 Mesh 缺少 position: mesh=${mesh.uuid}`)
      vertexOffsets.set(mesh, vertexCount)
      vertexCount += position.count
    }
    const piecesByFace = new Map<number, PuppyBodyTopologyTrianglePiece[]>()
    let indexCount = 0
    for (const entry of entries) {
      const candidate = entry.sourceAsset.topologySnapshot
      if (candidate.faces.length !== snapshot.faces.length || candidate.edges.length !== snapshot.edges.length
        || candidate.vertices.length !== snapshot.vertices.length
        || candidate.faces.some((face, index) => face.index !== snapshot.faces[index]?.index || face.ref !== snapshot.faces[index]?.ref)) {
        throw new Error(`PuppyCAD 同一 Body 的完整拓扑身份不一致: body=${entry.runtimeBinding.bodyBindingId}`)
      }
      entry.runtimeBinding.topologyFaceTriangleRanges = this.resolveLocalRanges(entry, entries)
      // 复用正式绑定对象的身份、越界和重叠校验，禁止重建器另写一套宽松规则。
      entry.runtimeAsset.meshData.topologySnapshot = snapshot
      const binding = new ImportedMeshTopologyBinding({
        mesh: entry.mesh, bodyBinding: entry.runtimeBinding, topologyAsset: entry.runtimeAsset,
        primitiveIndices: entry.runtimeBinding.primitiveIndices,
      })
      for (const range of entry.runtimeBinding.topologyFaceTriangleRanges) {
        const face = binding.resolveTopologyFaceByTriangleIndex(range.triangleRange[0])
        if (!face) throw new Error(`PuppyCAD 显示映射引用未知拓扑面: body=${entry.runtimeBinding.bodyBindingId}, face=${range.faceIndex}`)
        const pieces = piecesByFace.get(face.index) ?? []
        pieces.push({ mesh: entry.mesh, vertexOffset: vertexOffsets.get(entry.mesh)!, range: range.triangleRange })
        piecesByFace.set(face.index, pieces)
        indexCount += (range.triangleRange[1] - range.triangleRange[0]) * 3
      }
    }
    if (indexCount === 0) throw new Error(`PuppyCAD Body 没有可恢复的显示拓扑面片: body=${first.runtimeBinding.bodyBindingId}`)

    const positions = new Float32Array(vertexCount * 3)
    const normals = new Float32Array(vertexCount * 3)
    const indices = new Uint32Array(indexCount)
    const sourceToBody = new THREE.Matrix4()
    const normalMatrix = new THREE.Matrix3()
    const scratch = new THREE.Vector3()
    first.mesh.updateWorldMatrix(true, false)
    const worldToBody = first.mesh.matrixWorld.clone().invert()
    for (const mesh of meshes) {
      mesh.updateWorldMatrix(true, false)
      sourceToBody.multiplyMatrices(worldToBody, mesh.matrixWorld)
      normalMatrix.getNormalMatrix(sourceToBody)
      const position = mesh.geometry.getAttribute('position')
      const normal = mesh.geometry.getAttribute('normal')
      const offset = vertexOffsets.get(mesh)! * 3
      for (let index = 0; index < position.count; index += 1) {
        scratch.fromBufferAttribute(position, index).applyMatrix4(sourceToBody).toArray(positions, offset + index * 3)
        if (normal) scratch.fromBufferAttribute(normal, index).applyNormalMatrix(normalMatrix).toArray(normals, offset + index * 3)
      }
    }
    let cursor = 0
    const faces = snapshot.faces.map(face => {
      const start = cursor / 3
      for (const piece of piecesByFace.get(face.index) ?? []) {
        const sourceIndex = piece.mesh.geometry.getIndex()
        for (let index = piece.range[0] * 3; index < piece.range[1] * 3; index += 1) {
          indices[cursor++] = piece.vertexOffset + (sourceIndex ? sourceIndex.getX(index) : index)
        }
      }
      // 未显示的面保留身份和邻接语义，只将拾取区间设为空；不能删除并重新编号。
      return { ...face, triangleRange: [start, cursor / 3] as [number, number] }
    })
    const topologySnapshot: TopologySnapshot = { ...snapshot, faces }
    const faceTriangleRanges = faces.map(face => face.triangleRange)
    for (const entry of entries) {
      Object.assign(entry.runtimeAsset.meshData, { positions, normals, indices, topologySnapshot, faceTriangleRanges })
    }
  }
}
